import React from 'react';

import DisabledSecureIcon from '../../../../assets/icons/header/checkout/disabledSecure.svg';
import SecureIcon from '../../../../assets/icons/header/checkout/secure.svg';
import notepadIcon from '../../../../assets/icons/invoice-confirmation/notepad.svg';
import Button from '../../../../shared/components/Button';
import { PAYMENT_TEXTS } from '../../constants/payment.constants';
import PaymentMethodItem from '../shared/PaymentMethodItem';
import PaymentTerms from '../shared/PaymentTerms';

import { useInvoicePayment } from './hooks/useInvoicePayment';

interface InvoicePaymentFormProps {
    onPayLater: (email: string) => void;
    processing: boolean;
    error: string | null;
    expanded: boolean;
    onToggle: () => void;
    termsAccepted: boolean;
    setTermsAccepted: (accepted: boolean) => void;
}

const InvoicePaymentForm: React.FC<InvoicePaymentFormProps> = ({
    onPayLater,
    processing,
    error,
    expanded,
    onToggle,
    termsAccepted,
    setTermsAccepted,
}) => {
    const { email, setEmail, validationError, setValidationError, handlePayLater, isEmailValid } = useInvoicePayment({
        termsAccepted,
        onPayLater,
    });

    return (
        <PaymentMethodItem
            expanded={expanded}
            onToggle={onToggle}
            className="mb-6 font-nano"
        >
            <div className="flex items-center justify-between mb-[2px]">
                <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-0">Pay via Invoice</h3>
                    <p className="text-xs text-gray-500 mb-0">buy now, pay later</p>
                </div>
                {/* Invoice Icon */}
                <div className="flex items-center text-gray-900 gap-3">
                    <img src={notepadIcon} alt="Invoice" className="w-6 h-6" />
                </div>
            </div>

            {expanded && (
                <>
                    <hr className="mb-6 mt-6 border-gray-500" />

                    <div className="mb-6">
                        <label className="block text-sm font-medium mb-2 text-gray-500">{PAYMENT_TEXTS.INVOICE.EMAIL_LABEL}</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (validationError) setValidationError('');
                            }}
                            placeholder={PAYMENT_TEXTS.INVOICE.EMAIL_PLACEHOLDER}
                            className={`w-full px-4 h-12 border rounded-md focus:outline-none focus:border-[#030407] ${validationError ? 'border-red-500' : 'border-[#D0D5DD]'}`}
                        />
                        {validationError && <p className="text-red-500 text-sm mt-1">{validationError}</p>}
                    </div>

                    <p className="text-sm text-gray-500 mb-4 leading-relaxed">
                        {PAYMENT_TEXTS.INVOICE.INSTRUCTIONS}
                    </p>
                    <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                        {PAYMENT_TEXTS.INVOICE.WARNING}
                    </p>

                    {/* Terms & Conditions */}
                    <PaymentTerms
                        termsAccepted={termsAccepted}
                        setTermsAccepted={setTermsAccepted}
                        id="terms-checkbox-invoice"
                        className="mb-6 mt-2"
                    />

                    <Button
                        variant="primary"
                        onClick={handlePayLater}
                        disabled={processing || !email || !isEmailValid(email) || !termsAccepted}
                        className="w-full h-[48px] px-[20px] rounded-[6px] flex items-center justify-center gap-[6px] font-noto !font-bold !text-[14px] leading-[20px] disabled:bg-[#EAECF0] disabled:!text-[#667085] disabled:opacity-100 disabled:cursor-not-allowed bg-[#030407] text-white hover:bg-black"
                    >
                        {processing ? (
                            <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        ) : (
                            <>
                                <img
                                    src={(processing || !email || !isEmailValid(email)) ? DisabledSecureIcon : SecureIcon}
                                    alt="Secure"
                                    className={`w-[20px] h-[20px] ${(processing || !email || !isEmailValid(email)) ? '' : 'brightness-0 invert'}`}
                                />
                                <span>Pay later</span>
                            </>
                        )}
                    </Button>
                    {error && (
                        <p className="text-red-500 text-sm mt-4 text-center">{error}</p>
                    )}
                </>
            )}
        </PaymentMethodItem>
    );
};

export default InvoicePaymentForm;
